import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Alert,
  App,
  AutoComplete,
  Button,
  Checkbox,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import {
  DeleteOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  ImportOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  QrcodeOutlined,
  SaveOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import { API } from '@/services/api.service';
import { CALC_KINDS, CURRENCY_OPTIONS, STATUS_COLORS, coveredPeriodOf, date, errorText, fold, money, saveBlob } from './format';
import PppdXmlModal from './PppdXmlModal';
import RecipientSheetModal from './RecipientSheetModal';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/** Form values from a saved calculation; the form works with the net per month. */
const toForm = (c) => ({
  kind: c.kind,
  subject: c.subject,
  project: c.project?._id || null,
  property: c.property || null,
  coveredPeriod: c.coveredPeriod,
  jointPayment: c.jointPayment,
  month: c.period.month,
  year: c.period.year,
  months: c.months,
  currency: c.currency,
  rateDate: c.rateDate ? dayjs(c.rateDate) : null,
  exchangeRate: c.exchangeRate,
  payerAccount: c.payerAccount,
  netPurpose: c.netPurpose,
  note: c.note,
  recipients: c.recipients.map((r) => ({
    recipient: r.recipient || null,
    name: r.name,
    idNumber: r.idNumber,
    address: r.address,
    account: r.account,
    accountHolder: r.accountHolder,
    // An unfinished calculation may not have the net yet.
    netPerMonth: r.net === null || r.net === undefined ? null : Math.round((r.net / (c.months || 1)) * 100) / 100,
  })),
});

const fromForm = (v) => ({
  kind: v.kind,
  subject: v.subject,
  project: v.project || null,
  property: v.kind === 'rent' ? v.property || null : null,
  coveredPeriod: v.kind === 'rent' ? v.coveredPeriod || null : null,
  jointPayment: v.kind === 'rent' && (v.recipients || []).length > 1 && Boolean(v.jointPayment),
  period: { month: v.month, year: v.year },
  months: v.months,
  currency: v.currency,
  rateDate: v.rateDate ? v.rateDate.format('YYYY-MM-DD') : null,
  exchangeRate: v.currency === 'RSD' ? 1 : v.exchangeRate,
  payerAccount: v.payerAccount || null,
  netPurpose: v.kind === 'rent' ? v.netPurpose || null : null,
  note: v.note || null,
  recipients: (v.recipients || []).map((r) => ({
    ...r,
    recipient: r.recipient || null,
    account: v.kind === 'rent' ? r.account || null : null,
    accountHolder: v.kind === 'rent' && r.account ? r.accountHolder || null : null,
  })),
});

/** A form row nobody typed anything into: no recipient. */
const blankRow = (r) =>
  !r || ['recipient', 'name', 'idNumber', 'account', 'netPerMonth'].every((k) => r[k] === undefined || r[k] === null || r[k] === '');

/**
 * One withholding tax calculation (rent, other income or supplementary work). HLB's accountants
 * enter it, save it as a draft to see the figures, file the return in
 * ePorezi from them and type back the BOP and UID; then it is done and its
 * payment orders, with QR codes, carry the BOP. Clients see it read-only.
 *
 * „Save" keeps a calculation not yet complete (no rate, an amount missing)
 * without computing anything, so nothing typed is lost; „Calculate" checks it
 * in full. Closing with unsaved changes asks first, and a click beside the
 * drawer does not close it. People and amounts can come from a client's
 * Excel sheet (RecipientSheetModal).
 *
 * @param {string|null} id - the calculation, or null for a new one
 * @param {object|null} template - a calculation to start a new one from (the next month of the same flat)
 */
export default function CalculationDrawer({
  open,
  company,
  id,
  template,
  projects,
  payerAccounts = [],
  subjects = [],
  latestBySubject = {},
  canManage,
  onClose,
  onSaved,
  onSlip,
  onOpenReturn,
}) {
  const { t, i18n } = useTranslation('taxReturns');
  const { modal, message } = App.useApp();
  const [form] = Form.useForm();
  const [doneForm] = Form.useForm();
  const [currentId, setCurrentId] = useState(id);
  const [xmlOpen, setXmlOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Something typed since the drawer opened or was last saved; closing then asks first.
  const [dirty, setDirty] = useState(false);
  const kind = Form.useWatch('kind', form);
  const currency = Form.useWatch('currency', form);
  const rows = Form.useWatch('recipients', form) || [];

  // The company's people, so a recipient is picked rather than typed again.
  const { data: people = [] } = useQuery({
    queryKey: ['tax-recipients', company, 'active'],
    queryFn: () => API.taxReturns.getRecipients(company).then((d) => d.items),
    enabled: Boolean(open && company && canManage),
  });
  const peopleOptions = people
    .filter((p) => p.active && p.kinds.includes(kind || 'rent'))
    .map((p) => ({
      value: p._id,
      label: `${p.name}${p.idNumber ? ` · ${p.idNumber}` : ''}`,
      search: fold(`${p.name} ${p.idNumber}`),
    }));
  const pickPerson = (index, id) => {
    const p = people.find((x) => x._id === id);
    if (!p) return;
    const list = [...(form.getFieldValue('recipients') || [])];
    list[index] = {
      ...list[index],
      recipient: p._id,
      name: p.name,
      idNumber: p.idNumber,
      address: p.address,
      account: p.account,
      accountHolder: p.accountHolder,
    };
    form.setFieldValue('recipients', list);
    setDirty(true);
  };

  // The sheet's people go after the ones already typed; an empty row left in the form makes room for them.
  const addImported = (imported, { created, linked }) => {
    const kept = (form.getFieldValue('recipients') || []).filter((r) => !blankRow(r));
    form.setFieldValue('recipients', [
      ...kept,
      ...imported.map((p) => ({
        recipient: p.recipient,
        name: p.name,
        idNumber: p.idNumber,
        address: p.address,
        account: p.account,
        accountHolder: p.accountHolder,
        netPerMonth: p.netPerMonth,
      })),
    ]);
    setDirty(true);
    message.success(t('calc.sheet.added', { count: imported.length, created, linked }));
  };

  useEffect(() => setCurrentId(id), [id, open]);
  useEffect(() => {
    if (open) setDirty(false);
  }, [open, currentId]);

  const { data: c, refetch } = useQuery({
    queryKey: ['tax-calculation', company, currentId],
    queryFn: () => API.taxReturns.getCalculation(company, currentId),
    enabled: Boolean(open && currentId),
  });

  const loadRate = async (cur, day) => {
    if (!cur || cur === 'RSD' || !day) return;
    try {
      const { rate } = await API.taxReturns.getExchangeRate(cur, day.format('YYYY-MM-DD'));
      if (rate) form.setFieldValue('exchangeRate', rate);
    } catch {
      /* the accountant types it in */
    }
  };

  // A new calculation starts empty, or from the template with the next month; a saved one from its data.
  useEffect(() => {
    if (!open) return;
    if (currentId && c) {
      form.setFieldsValue(toForm(c));
      doneForm.setFieldsValue({ bop: c.bop, returnNumber: c.returnNumber });
      return;
    }
    if (!currentId) {
      const now = dayjs();
      const base = template ? toForm(template) : null;
      const next = template
        ? dayjs(`${template.period.year}-${template.period.month}-01`).add(template.months || 1, 'month')
        : now;
      form.resetFields();
      form.setFieldsValue({
        kind: 'rent',
        currency: 'EUR',
        months: 1,
        recipients: [{}],
        ...base,
        month: next.month() + 1,
        year: next.year(),
        coveredPeriod: base ? coveredPeriodOf(next.month() + 1, next.year(), base.months) : null,
        rateDate: now,
        netPurpose: null,
        note: null,
      });
      doneForm.resetFields();
      loadRate(base?.currency || 'EUR', now);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentId, c, template]);

  const saved = (data) => {
    setDirty(false);
    // A new calculation gets its id here, and the new query key loads it; refetching now would ask for `null`.
    if (currentId === data._id) refetch();
    else setCurrentId(data._id);
    onSaved?.();
  };

  // `incomplete`: „Save" — kept as typed, nothing checked in full or computed.
  const save = useMutation({
    mutationFn: ({ values, incomplete = false }) => {
      const body = { ...fromForm(values), incomplete };
      return currentId
        ? API.taxReturns.updateCalculation(company, currentId, body)
        : API.taxReturns.createCalculation(company, body);
    },
    onSuccess: (data, { incomplete }) => {
      saved(data);
      if (incomplete) message.success(t('calc.savedIncomplete'));
    },
  });

  const saveIncomplete = () => save.mutate({ values: form.getFieldsValue(true), incomplete: true });

  // X, Esc and „Cancel" all come here; a click beside the drawer does nothing (maskClosable).
  const requestClose = () => {
    if (!dirty || !canManage) {
      onClose();
      return;
    }
    modal.confirm({
      title: t('calc.leaveTitle'),
      content: t('calc.leaveText'),
      okText: t('calc.leaveOk'),
      okButtonProps: { danger: true, shape: 'round' },
      cancelText: t('calc.leaveCancel'),
      cancelButtonProps: { shape: 'round' },
      onOk: () => {
        setDirty(false);
        onClose();
      },
    });
  };

  const done = useMutation({
    mutationFn: (values) => API.taxReturns.setCalculationDone(company, currentId, values),
    onSuccess: saved,
  });

  const workbook = useMutation({
    mutationFn: () => API.taxReturns.exportCalculationWorkbook(company, currentId, i18n.language),
    onSuccess: ({ blob, fileName }) => saveBlob(blob, fileName),
  });

  const journal = useMutation({
    mutationFn: () => API.taxReturns.exportCalculationAbJournal(company, currentId),
    onSuccess: ({ blob, fileName }) => saveBlob(blob, fileName),
  });

  const remove = useMutation({
    mutationFn: () => API.taxReturns.deleteCalculation(company, currentId),
    onSuccess: () => {
      onSaved?.();
      onClose();
    },
  });

  const isDone = c?.status === 'done';
  const editable = canManage && !isDone;
  // Saved with „Save", not yet calculated: no figures, nothing to file or export.
  const incomplete = Boolean(c?.incomplete);
  const offerSave = !currentId || incomplete;
  // A landlord without an account gets no rent order, only the tax one.
  const noAccount = c?.kind === 'rent' ? c.recipients.filter((r) => !r.account).map((r) => r.name) : [];
  // Supplementary work also carries the PIO contribution, paid with the tax.
  const hasPio = Boolean(c?.pioRate);
  const lateInterest = c?.filing?.type === 3 && Boolean((c.filing.interest || 0) + (c.filing.interestPio || 0));
  const kindLabel = (k) => t(`calc.kinds.${k}`);
  const statusLabel = (s) => t(`status.${s}`, { context: canManage ? undefined : 'client' });

  // Picking a flat used before fills in its landlord, amounts and accounts from the last calculation.
  const pickSubject = (value) => {
    const last = latestBySubject[value];
    if (!last || currentId) return;
    const values = toForm(last);
    // The period and the rate date stay as picked for the new month.
    ['month', 'year', 'rateDate'].forEach((k) => delete values[k]);
    form.setFieldsValue(values);
  };

  const results = c && !incomplete && (
    <>
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label={t('calc.fields.kind')}>{kindLabel(c.kind)}</Descriptions.Item>
        <Descriptions.Item label={t('fields.project')}>{c.project ? `${c.project.code} ${c.project.name}` : '—'}</Descriptions.Item>
        <Descriptions.Item label={t('calc.fields.subject')} span={2}>
          {c.subject}
        </Descriptions.Item>
        <Descriptions.Item label={t('fields.purpose')}>{c.pid}</Descriptions.Item>
        <Descriptions.Item label={t('calc.fields.months')}>
          {c.months}
          {c.coveredPeriod ? ` (${c.coveredPeriod})` : ''}
        </Descriptions.Item>
        {c.currency !== 'RSD' && (
          <Descriptions.Item label={t('calc.fields.exchangeRate')} span={2}>
            {c.exchangeRate} ({c.currency}, {date(c.rateDate)})
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t('calc.fields.payerAccount')} span={2}>
          {c.payerAccount || '—'}
        </Descriptions.Item>
      </Descriptions>
      <Table
        size="small"
        rowKey={(r, i) => `${r.name}-${i}`}
        pagination={false}
        dataSource={c.recipients}
        scroll={{ x: true }}
        columns={[
          { title: t('calc.fields.recipient'), dataIndex: 'name' },
          c.currency !== 'RSD' && {
            title: `${t('calc.fields.net')} ${c.currency}`,
            dataIndex: 'net',
            align: 'right',
            render: money,
          },
          { title: t('calc.fields.netRsd'), dataIndex: 'netRsd', align: 'right', render: money },
          { title: t('calc.fields.gross'), dataIndex: 'gross', align: 'right', render: money },
          {
            title: t('calc.fields.standardizedCosts', { rate: Math.round(c.standardizedCostsRate * 100) }),
            dataIndex: 'standardizedCosts',
            align: 'right',
            render: money,
          },
          { title: t('calc.fields.taxBase'), dataIndex: 'taxBase', align: 'right', render: money },
          {
            title: t('calc.fields.tax', { rate: Math.round(c.taxRate * 100) }),
            dataIndex: 'tax',
            align: 'right',
            render: (v) => <b>{money(v)}</b>,
          },
          hasPio && { title: t('calc.fields.contributionBase'), dataIndex: 'contributionBase', align: 'right', render: money },
          hasPio && {
            title: t('calc.fields.pio', { rate: Math.round(c.pioRate * 100) }),
            dataIndex: 'pio',
            align: 'right',
            render: (v) => <b>{money(v)}</b>,
          },
        ].filter(Boolean)}
        summary={() =>
          c.recipients.length > 1 ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <b>{t('summary.total')}</b>
              </Table.Summary.Cell>
              {c.currency !== 'RSD' && <Table.Summary.Cell index={1} />}
              {['netRsd', 'gross', null, 'taxBase', 'tax', ...(hasPio ? ['contributionBase', 'pio'] : [])].map((k, i) => (
                <Table.Summary.Cell key={i} index={i + 2} align="right">
                  {k ? <b>{money(c.totals[k])}</b> : null}
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          ) : null
        }
      />
      {/* A late return also pays interest on the tax and on PIO, at the same rate and days, under the same BOP. */}
      {lateInterest && (
        <p className="mb-0">
          {t('calc.fields.interestTax')}: <b>{money(c.filing.interest || 0)}</b>
          {hasPio && (
            <>
              {' · '}
              {t('calc.fields.interestPio')}: <b>{money(c.filing.interestPio || 0)}</b>
            </>
          )}
        </p>
      )}
      {(hasPio || lateInterest) && (
        <p className="mb-0">
          {t(lateInterest ? (hasPio ? 'calc.fields.payableInterest' : 'calc.fields.payableTaxInterest') : 'calc.fields.payable')}:{' '}
          <b>{money(c.totals.payable)}</b>
        </p>
      )}
      <p className="text-xs opacity-60">
        {t(hasPio ? 'calc.coefficientNotePio' : 'calc.coefficientNote', { coefficient: c.coefficient })}
      </p>
    </>
  );

  return (
    <Drawer
      open={open}
      onClose={requestClose}
      maskClosable={false}
      width={880}
      destroyOnClose
      title={c ? `${kindLabel(c.kind)}: ${c.subject || '—'} · ${c.pid}` : t('calc.newTitle')}
      extra={
        c &&
        (incomplete ? (
          <Tag color="orange">{t('calc.incomplete')}</Tag>
        ) : (
          <Tag color={isDone ? 'green' : 'default'}>{t(`calc.status.${c.status}`)}</Tag>
        ))
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {editable && (
          <Form
            form={form}
            layout="vertical"
            onFinish={(v) => save.mutate({ values: v })}
            onValuesChange={() => setDirty(true)}
          >
            <Space wrap align="start">
              <Form.Item name="kind" label={t('calc.fields.kind')} rules={[{ required: true }]}>
                <Select style={{ width: 170 }} options={CALC_KINDS.map((k) => ({ value: k, label: kindLabel(k) }))} />
              </Form.Item>
              <Form.Item
                name="subject"
                label={t('calc.fields.subject')}
                tooltip={t('calc.help.subject')}
                rules={[{ required: true, message: t('errors.calc_subject_required') }]}
              >
                <AutoComplete
                  style={{ width: 320 }}
                  options={subjects.map((s) => ({ value: s }))}
                  onSelect={pickSubject}
                  filterOption={(input, o) => o.value.toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
              <Form.Item name="project" label={t('fields.project')}>
                <Select
                  allowClear
                  style={{ width: 220 }}
                  options={projects.map((p) => ({ value: p._id, label: `${p.code} ${p.name}` }))}
                />
              </Form.Item>
            </Space>

            <Form.Item name="property" hidden>
              <Input />
            </Form.Item>
            <Space wrap align="start">
              <Form.Item name="month" label={t('filters.month')} tooltip={t('calc.help.period')} rules={[{ required: true }]}>
                <Select style={{ width: 90 }} options={MONTHS.map((m) => ({ value: m, label: m }))} />
              </Form.Item>
              <Form.Item name="year" label={t('filters.year')} rules={[{ required: true }]}>
                <InputNumber style={{ width: 100 }} min={2000} max={2100} />
              </Form.Item>
              <Form.Item name="months" label={t('calc.fields.months')} tooltip={t('calc.help.months')} rules={[{ required: true }]}>
                <InputNumber style={{ width: 110 }} min={1} max={36} precision={0} />
              </Form.Item>
              {kind === 'rent' && (
                <Form.Item name="coveredPeriod" label={t('calc.fields.coveredPeriod')} tooltip={t('calc.help.coveredPeriod')}>
                  <Input style={{ width: 150 }} placeholder="08-10/2026" />
                </Form.Item>
              )}
              <Form.Item name="currency" label={t('calc.fields.currency')}>
                <Select
                  style={{ width: 90 }}
                  options={CURRENCY_OPTIONS}
                  onChange={(v) => loadRate(v, form.getFieldValue('rateDate'))}
                />
              </Form.Item>
              {currency !== 'RSD' && (
                <>
                  <Form.Item name="rateDate" label={t('calc.fields.rateDate')} tooltip={t('calc.help.rate')}>
                    <DatePicker format="DD.MM.YYYY" onChange={(d) => loadRate(form.getFieldValue('currency'), d)} />
                  </Form.Item>
                  <Form.Item
                    name="exchangeRate"
                    label={t('calc.fields.exchangeRate')}
                    rules={[{ required: true, message: t('errors.calc_invalid_rate') }]}
                  >
                    <InputNumber style={{ width: 130 }} min={0} step={0.0001} decimalSeparator="," />
                  </Form.Item>
                </>
              )}
            </Space>

            <Divider orientation="left" plain>
              {t(kind === 'rent' ? 'calc.landlord' : 'calc.recipients')}
            </Divider>
            <Form.List name="recipients">
              {(fields, { add, remove: removeRow }) => (
                <>
                  {fields.map(({ key, name }) => (
                    <Space key={key} wrap align="start">
                      <Form.Item name={[name, 'recipient']} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item label={t('calc.fields.fromList')}>
                        <Select
                          showSearch
                          allowClear
                          style={{ width: 200 }}
                          placeholder={t('calc.pickRecipient')}
                          value={rows[name]?.recipient || undefined}
                          options={peopleOptions}
                          filterOption={(input, o) => o.search.includes(fold(input))}
                          onChange={(id) =>
                            id ? pickPerson(name, id) : form.setFieldValue(['recipients', name, 'recipient'], null)
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        name={[name, 'name']}
                        label={t('calc.fields.recipient')}
                        rules={[{ required: true, message: t('errors.calc_recipient_required') }]}
                      >
                        <Input style={{ width: 200 }} />
                      </Form.Item>
                      <Form.Item name={[name, 'idNumber']} label={t('calc.fields.idNumber')}>
                        <Input style={{ width: 150 }} />
                      </Form.Item>
                      {kind === 'rent' && (
                        <Form.Item name={[name, 'account']} label={t('calc.fields.account')}>
                          <Input style={{ width: 200 }} placeholder="160-0000000000000-00" />
                        </Form.Item>
                      )}
                      {kind === 'rent' && rows[name]?.account && (
                        <Form.Item
                          name={[name, 'accountHolder']}
                          label={t('calc.fields.accountHolder')}
                          tooltip={t('people.helpText.accountHolder')}
                        >
                          <Input style={{ width: 180 }} />
                        </Form.Item>
                      )}
                      <Form.Item
                        name={[name, 'netPerMonth']}
                        label={t('calc.fields.netPerMonth', { currency: currency || 'EUR' })}
                        rules={[{ required: true, message: t('errors.calc_recipient_required') }]}
                      >
                        <InputNumber style={{ width: 140 }} min={0} step={0.01} decimalSeparator="," />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Button
                          shape="round"
                          type="text"
                          icon={<MinusCircleOutlined />}
                          style={{ marginTop: 30 }}
                          onClick={() => removeRow(name)}
                        />
                      )}
                    </Space>
                  ))}
                  {/* A block of its own: an inline Space would sit on the payer account's line and overlap its label. */}
                  <div style={{ marginBottom: 16 }}>
                    <Space wrap>
                      <Button shape="round" icon={<PlusOutlined />} onClick={() => add({})}>
                        {t(kind === 'rent' ? 'calc.addOwner' : 'calc.addRecipient')}
                      </Button>
                      <Button shape="round" icon={<ImportOutlined />} onClick={() => setSheetOpen(true)}>
                        {t('calc.sheet.button')}
                      </Button>
                    </Space>
                  </div>
                </>
              )}
            </Form.List>
            {/* Outside the list (inside it the name would be prefixed), and always mounted so the saved value is kept. */}
            <Form.Item name="jointPayment" valuePropName="checked" hidden={kind !== 'rent' || rows.length < 2}>
              <Checkbox>{t('people.fields.jointPayment')}</Checkbox>
            </Form.Item>

            <Space wrap align="start">
              <Form.Item name="payerAccount" label={t('calc.fields.payerAccount')}>
                <AutoComplete
                  style={{ width: 260 }}
                  options={payerAccounts.map((a) => ({ value: a.value, label: a.label }))}
                  placeholder="170-0000000000000-00"
                />
              </Form.Item>
              {kind === 'rent' && (
                <Form.Item name="netPurpose" label={t('calc.fields.netPurpose')} tooltip={t('calc.help.netPurpose')}>
                  <Input style={{ width: 240 }} />
                </Form.Item>
              )}
            </Space>
            <Form.Item name="note" label={t('fields.note')}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
            </Form.Item>

            {save.isError && <Alert type="error" showIcon message={errorText(t, save.error)} style={{ marginBottom: 12 }} />}
            <Space>
              <Button shape="round" type="primary" htmlType="submit" loading={save.isPending && !save.variables?.incomplete}>
                {t(currentId && !incomplete ? 'calc.saveChanges' : 'calc.calculate')}
              </Button>
              {offerSave && (
                <Button
                  shape="round"
                  icon={<SaveOutlined />}
                  loading={save.isPending && Boolean(save.variables?.incomplete)}
                  onClick={saveIncomplete}
                  title={t('calc.help.saveIncomplete')}
                >
                  {t('calc.saveIncomplete')}
                </Button>
              )}
              {currentId && (
                <Popconfirm title={t('calc.deleteConfirm')} onConfirm={() => remove.mutate()}>
                  <Button shape="round" danger icon={<DeleteOutlined />} loading={remove.isPending}>
                    {t('calc.delete')}
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Form>
        )}

        {results && (
          <>
            {editable && (
              <Divider orientation="left" plain>
                {t('calc.result')}
              </Divider>
            )}
            {results}
          </>
        )}

        {c && incomplete && <Alert type="info" showIcon message={t('calc.incompleteNote')} />}

        {c && !incomplete && (
          <>
            <Divider orientation="left" plain>
              {t('calc.returnData')}
            </Divider>
            {canManage && (
              <Space wrap>
                <Button shape="round" icon={<FileTextOutlined />} onClick={() => setXmlOpen(true)}>
                  {t('pppd.button')}
                </Button>
                <Button shape="round" icon={<FileExcelOutlined />} loading={workbook.isPending} onClick={() => workbook.mutate()}>
                  {t('calc.excelButton')}
                </Button>
                {isDone && c.kind === 'rent' && (
                  <Button
                    shape="round"
                    icon={<SnippetsOutlined />}
                    loading={journal.isPending}
                    onClick={() => journal.mutate()}
                    title={t('calc.abJournalHelp')}
                  >
                    {t('calc.abJournalButton')}
                  </Button>
                )}
              </Space>
            )}
            {(workbook.isError || journal.isError) && (
              <Alert type="error" showIcon message={errorText(t, workbook.error || journal.error)} />
            )}
            {journal.data?.missingPartners?.length > 0 && (
              <Alert type="warning" showIcon message={t('calc.abMissingPartners', { names: journal.data.missingPartners.join(', ') })} />
            )}
            {canManage && noAccount.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={t(isDone ? 'calc.noLandlordAccountDone' : 'calc.noLandlordAccount', { names: noAccount.join(', ') })}
              />
            )}
            {editable ? (
              <Form form={doneForm} layout="vertical" onFinish={(v) => done.mutate({ ...v, done: true })}>
                <p className="mb-2 text-sm opacity-70">{t('calc.help.done')}</p>
                <Space wrap align="start">
                  <Form.Item name="bop" label={t('fields.bop')} rules={[{ required: true, message: t('errors.calc_bop_required') }]}>
                    <Input style={{ width: 230 }} maxLength={25} />
                  </Form.Item>
                  <Form.Item
                    name="returnNumber"
                    label={t('calc.fields.returnNumber')}
                    rules={[{ required: true, message: t('errors.calc_uid_required') }]}
                  >
                    <Input style={{ width: 230 }} />
                  </Form.Item>
                  <Button shape="round" type="primary" htmlType="submit" loading={done.isPending} style={{ marginTop: 30 }}>
                    {t('calc.markDone')}
                  </Button>
                </Space>
                {done.isError && <Alert type="error" showIcon message={errorText(t, done.error)} />}
              </Form>
            ) : (
              <Descriptions size="small" column={2} bordered>
                <Descriptions.Item label={t('fields.bop')}>{c.bop || '—'}</Descriptions.Item>
                <Descriptions.Item label={t('calc.fields.returnNumber')}>{c.returnNumber || '—'}</Descriptions.Item>
                <Descriptions.Item label={t('calc.fields.return')} span={2}>
                  {c.taxReturn ? (
                    <Space>
                      <Tag color={STATUS_COLORS[c.taxReturn.status]}>{statusLabel(c.taxReturn.status)}</Tag>
                      <span className="text-xs opacity-70">
                        {t('fields.dueDate')}: {date(c.taxReturn.dueDate)}
                      </span>
                      {onOpenReturn && (
                        <Button shape="round" size="small" onClick={() => onOpenReturn(c.taxReturn._id)}>
                          {t('calc.openReturn')}
                        </Button>
                      )}
                    </Space>
                  ) : (
                    <span className="text-xs opacity-70">{t('calc.noReturnYet')}</span>
                  )}
                </Descriptions.Item>
              </Descriptions>
            )}
          </>
        )}

        {isDone && c.orders.length > 0 && (
          <>
            <Divider orientation="left" plain>
              {t('calc.orders')}
            </Divider>
            <Table
              size="small"
              rowKey={(o, i) => `${o.kind}-${i}`}
              pagination={false}
              dataSource={c.orders}
              columns={[
                { title: t('calc.fields.orderKind'), dataIndex: 'kind', render: (k) => t(`calc.orderKinds.${k}`) },
                { title: t('slip.payee'), dataIndex: 'payee' },
                { title: t('fields.paymentAccount'), dataIndex: 'account' },
                { title: t('calc.fields.purposeText'), dataIndex: 'purpose' },
                { title: t('fields.amount'), dataIndex: 'amount', align: 'right', render: money },
                {
                  title: '',
                  width: 120,
                  render: (o) =>
                    o.qr ? (
                      <Button
                        shape="round"
                        size="small"
                        icon={<QrcodeOutlined />}
                        onClick={() =>
                          onSlip?.({
                            title: `${t(`calc.orderKinds.${o.kind}`)}: ${c.subject}`,
                            slip: { ...o, dueDate: c.taxReturn?.dueDate || null },
                          })
                        }
                      >
                        {t('slip.button')}
                      </Button>
                    ) : null,
                },
              ]}
            />
          </>
        )}

        {isDone && canManage && (
          <Popconfirm title={t('calc.reopenConfirm')} onConfirm={() => done.mutate({ done: false })}>
            <Button shape="round">{t('calc.reopen')}</Button>
          </Popconfirm>
        )}
      </Space>
      <PppdXmlModal open={xmlOpen} company={company} calculation={c} onClose={() => setXmlOpen(false)} />
      {editable && (
        <RecipientSheetModal
          open={sheetOpen}
          company={company}
          kind={kind || 'other_income'}
          currency={currency}
          existingIds={rows.map((r) => r?.idNumber).filter(Boolean)}
          onClose={() => setSheetOpen(false)}
          onImported={addImported}
        />
      )}
    </Drawer>
  );
}
